/**
 * @module services/initializationService
 * @description Centralized application initialization with error handling
 *
 * Manages the application startup sequence with:
 * - Phased initialization (storage → locale → auth → data)
 * - Centralized error handling and recovery
 * - Parallel loading where dependencies allow
 * - Observable initialization state
 *
 * @example
 * import { initializeApp, initState } from '@services/initializationService';
 *
 * // Start initialization
 * const result = await initializeApp();
 *
 * // Check state reactively
 * $: isReady = $initState.phase === 'complete';
 */

import { writable, derived, get } from 'svelte/store';
import { createLogger } from './loggerService';
import { notify } from './notificationService';
import { indexedDBService } from './indexedDBService';
import { initLocale } from '@/i18n';
import {
  initAuth,
  isAuthenticated,
  userId,
  isProxyMode,
  isSupabaseAuthenticated,
  isZoteroConfigured,
} from '@stores/authStore';
import {
  initLibraryStore,
  loadGroups,
  selectedLibraryId,
  selectedLibraryType,
} from '@stores/libraryStore';
import { loadCollections } from '@stores/collectionStore';
import { loadItems } from '@stores/itemStore';
import { initUIStore } from '@stores/uiStore';
import { api } from '@/config';

const log = createLogger('init');

// =============================================================================
// TYPES
// =============================================================================

/** Initialization phases */
export type InitPhase = 'idle' | 'storage' | 'locale' | 'auth' | 'data' | 'complete' | 'error';

/** Initialization state */
export interface InitState {
  /** Current phase */
  phase: InitPhase;
  /** Whether initialization is in progress */
  isLoading: boolean;
  /** Error message if failed */
  error: string | null;
  /** Phase-specific errors (non-critical) */
  warnings: string[];
  /** Whether user needs to authenticate */
  needsAuth: boolean;
  /** Redirect URL for proxy mode auth */
  redirectUrl: string | null;
}

/** Initialization result */
export interface InitResult {
  success: boolean;
  needsAuth: boolean;
  redirectUrl?: string;
  error?: string;
}

// =============================================================================
// STORE
// =============================================================================

const initialState: InitState = {
  phase: 'idle',
  isLoading: false,
  error: null,
  warnings: [],
  needsAuth: false,
  redirectUrl: null,
};

const store = writable<InitState>(initialState);

/** Initialization state store */
export const initState = {
  subscribe: store.subscribe,
};

/** Whether app is fully initialized */
export const isInitialized = derived(store, ($store) => $store.phase === 'complete');

/** Whether initialization failed */
export const hasInitError = derived(store, ($store) => $store.phase === 'error');

// =============================================================================
// PHASE HANDLERS
// =============================================================================

/**
 * Phase 1: Initialize storage (IndexedDB)
 */
async function initStorage(): Promise<void> {
  log.info('Phase 1: Initializing storage');
  store.update((s) => ({ ...s, phase: 'storage' }));

  try {
    await indexedDBService.init();
    log.debug('Storage initialized');
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Storage initialization failed';
    log.error('Storage init failed', { error });
    // Storage failure is critical
    throw new Error(`Storage: ${message}`);
  }
}

/**
 * Phase 2: Initialize locale and UI (synchronous)
 */
function initLocaleAndUI(): void {
  log.info('Phase 2: Initializing locale and UI');
  store.update((s) => ({ ...s, phase: 'locale' }));

  try {
    initLocale();
    initUIStore();
    log.debug('Locale and UI initialized');
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Locale/UI initialization failed';
    log.warn('Locale/UI init warning', { error });
    // Non-critical - add warning but continue
    store.update((s) => ({ ...s, warnings: [...s.warnings, message] }));
  }
}

/**
 * Phase 3: Initialize authentication
 */
async function initAuthentication(): Promise<{
  authenticated: boolean;
  needsAuth: boolean;
  redirectUrl?: string;
}> {
  log.info('Phase 3: Initializing authentication');
  store.update((s) => ({ ...s, phase: 'auth' }));

  try {
    await initAuth();

    const authenticated = get(isAuthenticated);
    const proxyMode = get(isProxyMode);

    if (authenticated) {
      log.debug('User authenticated');
      return { authenticated: true, needsAuth: false };
    }

    // Handle proxy mode redirects
    if (proxyMode) {
      const portalUrl = api.proxy.portalUrl;
      const returnTo = encodeURIComponent(window.location.href.split('#')[0]);
      const supabaseAuth = get(isSupabaseAuthenticated);
      const zoteroConfigured = get(isZoteroConfigured);

      if (!supabaseAuth) {
        const redirectUrl = `${portalUrl}/?returnTo=${returnTo}`;
        log.info('Proxy mode: redirecting to Portal login');
        return { authenticated: false, needsAuth: true, redirectUrl };
      }

      if (!zoteroConfigured) {
        // Redirect to cloud domain settings page
        const cloudUrl = portalUrl.replace('auth.', 'cloud.');
        const redirectUrl = `${cloudUrl}/settings`;
        log.info('Proxy mode: redirecting to Portal settings');
        return { authenticated: false, needsAuth: true, redirectUrl };
      }
    }

    // Direct mode or fallback: needs auth modal
    log.debug('Authentication required');
    return { authenticated: false, needsAuth: true };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Authentication failed';
    log.error('Auth init failed', { error });
    // Auth failure is not critical - user can retry
    store.update((s) => ({ ...s, warnings: [...s.warnings, `Auth: ${message}`] }));
    return { authenticated: false, needsAuth: true };
  }
}

/**
 * Phase 4: Load initial data (groups, collections, items)
 */
async function initData(): Promise<void> {
  log.info('Phase 4: Loading initial data');
  store.update((s) => ({ ...s, phase: 'data' }));

  const userIdValue = get(userId);
  if (!userIdValue) {
    log.warn('No user ID available for data loading');
    return;
  }

  try {
    // Initialize library store and load groups
    initLibraryStore(userIdValue);
    await loadGroups(userIdValue);

    const libraryId = get(selectedLibraryId);
    const libraryType = get(selectedLibraryType);

    if (libraryId && libraryType) {
      // Load collections and items in parallel
      log.debug('Loading collections and items in parallel');
      const [collectionsResult, itemsResult] = await Promise.allSettled([
        loadCollections(libraryId, libraryType),
        loadItems(libraryId, libraryType),
      ]);

      // Check for failures (non-critical - data can be retried)
      if (collectionsResult.status === 'rejected') {
        const message = collectionsResult.reason?.message || 'Failed to load collections';
        log.warn('Collections load warning', { error: message });
        store.update((s) => ({ ...s, warnings: [...s.warnings, message] }));
      }

      if (itemsResult.status === 'rejected') {
        const message = itemsResult.reason?.message || 'Failed to load items';
        log.warn('Items load warning', { error: message });
        store.update((s) => ({ ...s, warnings: [...s.warnings, message] }));
      }
    }

    log.debug('Initial data loaded');
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to load data';
    log.error('Data loading failed', { error });
    // Data loading failure is not critical
    store.update((s) => ({ ...s, warnings: [...s.warnings, message] }));
  }
}

// =============================================================================
// MAIN INITIALIZATION
// =============================================================================

/**
 * Initialize the application
 *
 * Runs through all initialization phases with proper error handling:
 * 1. Storage (IndexedDB) - Critical
 * 2. Locale & UI - Non-critical
 * 3. Authentication - May require user action
 * 4. Data loading - Non-critical, can retry
 *
 * @returns Initialization result with success status and any required actions
 */
export async function initializeApp(): Promise<InitResult> {
  log.info('=== Starting Refero initialization ===');

  store.set({
    ...initialState,
    isLoading: true,
  });

  try {
    // Phase 1: Storage (critical)
    await initStorage();

    // Phase 2: Locale and UI (sync, non-critical)
    initLocaleAndUI();

    // Phase 3: Authentication
    const authResult = await initAuthentication();

    if (authResult.redirectUrl) {
      // Proxy mode redirect needed
      store.update((s) => ({
        ...s,
        isLoading: false,
        needsAuth: true,
        redirectUrl: authResult.redirectUrl ?? null,
        phase: 'complete',
      }));

      log.info('Initialization complete (redirect required)');
      return {
        success: true,
        needsAuth: true,
        redirectUrl: authResult.redirectUrl,
      };
    }

    if (authResult.authenticated) {
      // Phase 4: Data loading (parallel where possible)
      await initData();
    }

    // Complete
    store.update((s) => ({
      ...s,
      isLoading: false,
      needsAuth: authResult.needsAuth,
      phase: 'complete',
    }));

    const state = get(store);
    if (state.warnings.length > 0) {
      log.warn('Initialization complete with warnings', { warnings: state.warnings });
    } else {
      log.info('=== Refero initialization complete ===');
    }

    return {
      success: true,
      needsAuth: authResult.needsAuth,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Initialization failed';

    store.update((s) => ({
      ...s,
      isLoading: false,
      error: message,
      phase: 'error',
    }));

    log.error('=== Refero initialization FAILED ===', { error: message });
    notify.error(`Initialization failed: ${message}`);

    return {
      success: false,
      needsAuth: false,
      error: message,
    };
  }
}

/**
 * Retry data loading after authentication
 */
export async function retryDataLoading(): Promise<void> {
  const authenticated = get(isAuthenticated);
  if (!authenticated) {
    log.warn('Cannot retry data loading: not authenticated');
    return;
  }

  store.update((s) => ({ ...s, isLoading: true, warnings: [] }));

  try {
    await initData();
    store.update((s) => ({ ...s, isLoading: false }));
    log.info('Data loading retry successful');
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Data loading failed';
    store.update((s) => ({
      ...s,
      isLoading: false,
      warnings: [...s.warnings, message],
    }));
    log.error('Data loading retry failed', { error });
  }
}

/**
 * Reset initialization state
 */
export function resetInitState(): void {
  store.set({ ...initialState });
}

/**
 * Get current initialization state
 */
export function getInitState(): InitState {
  return get(store);
}
